import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import { AdminApi, AdminProductPrice, AdminProductsListPermissionEnum } from 'src/api';
import MarketPricesTable from './market-prices-table';
import DeleteMarketPriceModal from './delete-market-prices-modal';
import DeleteMarketPricesSuccessModal from './delete-market-prices-success-modal';
import AddMarketPricesModal from './add-market-prices';
import AddMarketPricesSuccessModal from './add-market-prices-success-modal';
import useApiCall from 'src/hooks/use-api-call';
import Tooltip, { TooltipPosition } from 'src/ui/atoms/tooltip';
import Button from 'src/ui/atoms/button';

interface MarketPricesProps {
  productId: string;
}

const MarketPrices: FunctionComponent<MarketPricesProps> = ({ productId }) => {
  const [deleteMarketPriceModalOpen, setDeleteMarketPriceModalOpen] = useState<AdminProductPrice | undefined>();
  const [isSuccessDeleteModalOpen, setSuccessDeleteModalOpen] = useState(false);
  const [isAddMarketPricesModalOpen, setAddMarketPricesModalOpen] = useState(false);
  const [isSuccessAddedModalOpen, setSuccessAddedModalOpen] = useState(false);
  const [forceDataLoad, setForceDataLoad] = useState(false);
  const [hasManageMarketPermission, setHasManageMarketPermission] = useState(false);

  const { withApi, makeAuthenticatedApi } = useApiCall();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const getMarketPricePermission = useCallback(() => {
    withApi(async () => {
      const response = await adminApi.adminProductsList({
        id: productId,
        permission: AdminProductsListPermissionEnum.MANAGE_MARKET_PRICES,
        limit: 1,
      });
      setHasManageMarketPermission(response.results.length > 0);
    });
  }, [productId, adminApi]);

  useEffect(() => {
    getMarketPricePermission();
  }, [productId]);

  const onMarketPriceDeleted = useCallback(() => {
    setDeleteMarketPriceModalOpen(undefined);
    setForceDataLoad(true);
    setSuccessDeleteModalOpen(true);
  }, [forceDataLoad]);

  const onMarketPriceAdded = useCallback(() => {
    setAddMarketPricesModalOpen(false);
    setForceDataLoad(true);
    setSuccessAddedModalOpen(true);
  }, [forceDataLoad]);

  return (
    <WideContent
      title={<Translate name="dashboard.issuerMarketPrices" />}
      ignorePageTitle
      titleSpacing="medium"
      headerPanel={
        !hasManageMarketPermission ? (
          <Tooltip content={<Translate name="marketPrices.noAddPermission" />} position={TooltipPosition.BOTTOM}>
            <Button variant="primary" size="small" icon="plus" iconColor="inherit" disabled={true}>
              <Translate name="marketPrices.addMarketPrices" />
            </Button>
          </Tooltip>
        ) : (
          <Button
            variant="primary"
            size="small"
            icon="plus"
            iconColor="inherit"
            onClick={() => setAddMarketPricesModalOpen(true)}
          >
            <Translate name="marketPrices.addMarketPrices" />
          </Button>
        )
      }
    >
      <MarketPricesTable
        productId={productId}
        hasManageMarketPermission={hasManageMarketPermission}
        onDelete={(marketPrice) => setDeleteMarketPriceModalOpen(marketPrice)}
        skipFilterUrlSync={true}
        forceDataLoad={forceDataLoad}
        setForceDataLoad={setForceDataLoad}
      />

      {!!deleteMarketPriceModalOpen && (
        <DeleteMarketPriceModal
          marketPrice={deleteMarketPriceModalOpen}
          onDeleteMarketPrice={onMarketPriceDeleted}
          onClose={() => setDeleteMarketPriceModalOpen(undefined)}
        />
      )}

      {isSuccessDeleteModalOpen && <DeleteMarketPricesSuccessModal onClose={() => setSuccessDeleteModalOpen(false)} />}

      {isAddMarketPricesModalOpen && (
        <AddMarketPricesModal
          onAddMarketPrice={onMarketPriceAdded}
          onClose={() => setAddMarketPricesModalOpen(false)}
          productId={productId}
        ></AddMarketPricesModal>
      )}

      {isSuccessAddedModalOpen && (
        <AddMarketPricesSuccessModal onClose={() => setSuccessAddedModalOpen(false)}></AddMarketPricesSuccessModal>
      )}
    </WideContent>
  );
};

export default MarketPrices;
